// See https://www.canadapost-postescanada.ca/ac/learn-more/simple-setup/ for API details
import React, { useState, InputHTMLAttributes } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import './pca_2.20.css';
import './pca_2.10';
import { ReactComponent as NotificationLogo } from '../../styles/img/icons/error.svg';
import ProvinceMismatchModal from '../modal/instances/provinceMismatchModal';
import { IFormModalRef } from '../modal/modal';
import config from '../../config';

declare module pca {}

export interface IPcaFieldProps extends InputHTMLAttributes<HTMLInputElement>, WithTranslation {
    fieldContainerClassName?: string;
    fieldName: string;
    fieldTitle: string;
    fieldId: string;
    fieldPca: string;
    fieldOptionalText?: string;
    fieldTooltip?: JSX.Element;
    fieldDisabled?: boolean;
    fieldDisabledPopover?: boolean;
    fieldRequired?: boolean;
    fieldHiddenLabel?: string;
    fieldWeight: number;
    fieldAddress: string;
    fieldCity: string;
    fieldPostalCode: string;
    fieldAccountProvinceCode: string;
}

const setFieldValue = (fieldName: string, fieldValue: string) => {
    (document.getElementById(fieldName)! as HTMLInputElement).value = fieldValue;
};

const PcaField = (props: IPcaFieldProps) => {
    const [touched, setTouched] = useState(false);
    const {
        t,
        i18n,
        tReady,
        fieldName,
        fieldTitle,
        fieldContainerClassName,
        fieldOptionalText,
        fieldTooltip,
        fieldDisabled,
        fieldDisabledPopover,
        fieldId,
        fieldPca,
        fieldRequired,
        fieldWeight,
        fieldAddress,
        fieldCity,
        fieldPostalCode,
        fieldHiddenLabel,
        fieldAccountProvinceCode,
        ...inputProps
    } = props;
    const ref = React.useRef<IFormModalRef | null>(null);
    const options = {
        key: config.PCA_API_KEY,
        languagePreference: i18n.language === 'fr' ? 'fra' : 'eng',
        culture: i18n.language === 'fr' ? 'fr-ca' : 'en-us',
        bar: {
            showCountry: false,
        },
    };

    const fields = [
        {
            element: fieldId,
            field: fieldPca,
            mode: 1, // pca.fieldMode.POPULATE
        },
    ];

    // @ts-ignore
    const addressComplete = new pca.Address(fields, options);
    addressComplete.listen('populate', function (address: any) {
        if (fieldAccountProvinceCode.toLowerCase() !== address.ProvinceCode.toLowerCase()) {
            // Display modal
            setFieldValue(fieldAddress, '');
            updateAddressFields();
            ref.current?.setModalOpen(true);
        } else {
            setFieldValue(fieldId, address.Line1);
            updateAddressFields(address);
        }
    });

    const updateAddressFields = (address: any = null) => {
        setFieldValue(fieldAddress, address?.Line1 ?? '');
        setFieldValue(fieldCity, address?.City ?? '');
        setFieldValue(fieldPostalCode, address?.PostalCode ?? '');
        setTouched(false);
    };

    const onBlur = () => {
        if (touched) {
            setTouched(false);
            updateAddressFields();
        }
    }

    const onChange = () => {
        document.getElementById(fieldName)?.classList.remove('registration-error');
        const errorLogo = document.getElementById(fieldName + '_error_logo');
        if (errorLogo) {
            errorLogo.style.display = 'none';
        }
        setTouched(true);
        updateAddressFields()
    }

    return (
        <>
            <ProvinceMismatchModal ref={ref}/>
            <div className={'field-container '.concat(fieldContainerClassName ?? '')}>
                <div className="field-label-container">
                    <label htmlFor={fieldName} className={'field-label field-label-container'}>
                        {fieldHiddenLabel && fieldDisabled && (
                            <span className="accessibility-hidden">{t(fieldHiddenLabel)}</span>
                        )}
                        {t(fieldTitle)}
                        {fieldOptionalText ? (
                            <span className="field-label--optional field-optional">
                                {''.addSpace() + t(fieldOptionalText)}
                            </span>
                        ) : null}
                    </label>
                    {!fieldDisabledPopover && fieldTooltip}
                </div>
                <input
                    {...inputProps}
                    name={fieldName}
                    id={fieldName}
                    readOnly={fieldDisabled}
                    className={'field-input field-value '.concat(
                        fieldDisabled ? 'field-input--disabled field-value--disabled' : ''
                    )}
                    data-set-required={fieldRequired}
                    aria-required={fieldRequired}
                    data-set-weight={fieldWeight}
                    aria-disabled={fieldDisabled}
                    onChange={onChange}
                    onBlur={onBlur}
                />
                <NotificationLogo id={fieldName + '_error_logo'} className="field-error-icon"/>
            </div>
        </>
    );
};
export default withTranslation()(PcaField);
